/**
 * Arche → Ownership (ADR-026): who owns this arche, and "Transfer to a
 * Company" for its creator.
 *
 * The offer says what the creator gives up before it is sent — a Company
 * owner still has to accept, on a screen that shows the Company who will be
 * paid and every collaborator who keeps access:
 *   - earnings go to the Company's payer, from acceptance on;
 *   - the Company's owners and admins govern the arche (team, access,
 *     settings) — only the Company can hand it back;
 *   - every owner becomes a publisher: the creator keeps publish access.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { describeCompanyError, isRequestRefusal } from '@/auth/lib/company-errors';
import {
  useArcheOwnership,
  useArcheTransferCompanies,
  useCancelArcheTransfer,
  useProposeArcheTransfer,
} from '@/auth/hooks/useArcheGovernance';

const REASON_MAX = 500;

export function ArcheOwnershipSection({ archeId }: { archeId: string }) {
  const ownership = useArcheOwnership(archeId);
  const [open, setOpen] = useState(false);
  const companies = useArcheTransferCompanies({ enabled: open });
  const propose = useProposeArcheTransfer(archeId);
  const cancel = useCancelArcheTransfer();
  const [companyId, setCompanyId] = useState('');
  const [reason, setReason] = useState('');

  // Not visible to this caller (or not a plugin): say nothing. A failure to
  // load is said, with a way to try again.
  if (ownership.isError && !isRequestRefusal(ownership.error)) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Ownership</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <span className="text-destructive">Could not load who owns this arche.</span>
          <Button size="sm" variant="outline" onClick={() => void ownership.refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (ownership.isLoading || ownership.isError || !ownership.data) return null;
  const data = ownership.data;

  async function send(): Promise<void> {
    try {
      await propose.mutateAsync({ companyId, reason: reason.trim() || undefined });
      toast({ title: 'Offer sent', description: 'A Company owner needs to accept it.' });
      setOpen(false);
      setReason('');
    } catch (err) {
      toast({
        title: 'Could not send the offer',
        description: describeCompanyError(err),
        variant: 'destructive',
      });
    }
  }

  const pending = data.pendingTransfer;

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Ownership</CardTitle>
        <CardDescription>Who owns this arche and is paid its earnings.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {data.owningCompany ? (
          <p>
            Owned by <span className="font-medium">{data.owningCompany.name ?? 'a Company'}</span>.
            Its owners and admins manage it from Company → Owned arches; publishers keep publishing.
          </p>
        ) : pending ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p>
              Offered to <span className="font-medium">{pending.companyName ?? 'a Company'}</span> —
              waiting for a Company owner. Lapses {new Date(pending.expiresAt).toLocaleDateString()}
              .
            </p>
            {/* Only the creator who offered it may withdraw; the rest of the team sees it. */}
            {data.isOwner === true && (
              <Button
                size="sm"
                variant="outline"
                disabled={cancel.isPending}
                onClick={async () => {
                  try {
                    await cancel.mutateAsync(pending.transferId);
                    toast({ title: 'Offer withdrawn' });
                  } catch (err) {
                    toast({
                      title: 'Could not withdraw',
                      description: describeCompanyError(err),
                      variant: 'destructive',
                    });
                  }
                }}
              >
                Withdraw offer
              </Button>
            )}
          </div>
        ) : data.canOfferToCompany ? (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            Transfer to a Company…
          </Button>
        ) : data.isOwner ? (
          <p className="text-muted-foreground">
            You own this arche. It is not active, so it cannot be offered to a Company.
          </p>
        ) : (
          <p className="text-muted-foreground">
            Owned by its creator, who is paid its earnings. You are on its team.
          </p>
        )}
      </CardContent>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Transfer this arche to a Company</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>If a Company owner accepts:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    its earnings go to the Company&apos;s payer, who also pays its per-arche costs
                    (asset library, content packs, generated docs), from then on;
                  </li>
                  <li>
                    the Company&apos;s owners and admins manage it — its team, who can use it, its
                    settings. Only the Company can hand it back;
                  </li>
                  <li>
                    you, and anyone else who owns it now, become publishers: you keep publishing it.
                  </li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {companies.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : companies.isError || !companies.data ? (
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <span className="text-destructive">Could not load your Companies.</span>
              <Button size="sm" variant="outline" onClick={() => void companies.refetch()}>
                Retry
              </Button>
            </div>
          ) : !companies.data.transferEnabled ? (
            <p className="text-sm text-muted-foreground">
              Moving arches to a Company is not available right now.
            </p>
          ) : companies.data.companies.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              You can offer an arche to a Company you belong to — as one of its people, or as a
              member of one of its workspaces.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="grid gap-1.5">
                <Label htmlFor={`transfer-company-${archeId}`}>Company</Label>
                <select
                  id={`transfer-company-${archeId}`}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                >
                  <option value="">Choose…</option>
                  {companies.data.companies.map((c) => (
                    <option key={c.companyId} value={c.companyId}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={`transfer-reason-${archeId}`}>
                  Note for the Company (optional)
                </Label>
                <Textarea
                  id={`transfer-reason-${archeId}`}
                  value={reason}
                  maxLength={REASON_MAX}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!companyId || propose.isPending || !companies.data?.transferEnabled}
              onClick={(e) => {
                e.preventDefault();
                void send();
              }}
            >
              Send offer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
